/** The header's word on the plan (spec §9.3, design v2 § 7): what you are on, two mini-meters,
 * and the numbers — which wrap out of view on a phone, where the account menu carries them.
 * The word opens the account page. */
import type { Entitlement, PlanKind } from "../api/api";
import { MiniMeters } from "./Meter";

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

/** Billing's `kind`, or — from a billing deployed before design v2, which deploy-web can
 * beat to the account — the same answer derived from the older fields. */
export function kindOf(e: Entitlement): PlanKind {
  if (e.kind) return e.kind;
  if (e.reason === "never_subscribed") return "none";
  if (!e.active) return "read_only";
  if (e.plan === "trial") return "trial";
  if (e.plan === "code") return "complimentary";
  return "paid";
}

function trialDays(e: Entitlement): number | null {
  if (e.trial_days_left !== undefined) return e.trial_days_left;
  if (!e.ends_at) return null;
  return Math.max(0, Math.ceil((Date.parse(e.ends_at) - Date.now()) / 86_400_000));
}

export function tierName(plan: string): string {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/** The header's short form. Every branch reads billing's `kind`, and the trial's days are
 * the server's count, so a skewed device clock cannot change what the header says. */
export function planLabel(e: Entitlement): string {
  switch (kindOf(e)) {
    case "none":
    case "read_only":
      return "Read-only";
    case "trial": {
      const n = trialDays(e);
      return n === null ? "Trial" : `Trial · ${n} ${n === 1 ? "day" : "days"} left`;
    }
    case "complimentary":
      return e.ends_at ? `Complimentary · until ${shortDate(e.ends_at)}` : "Complimentary";
    case "paid":
      return tierName(e.chosen_plan ?? e.plan);
  }
}

/** The account menu's second line: the plan in a sentence. */
export function planLine(e: Entitlement): string {
  const kind = kindOf(e);
  if (kind === "none") return "No plan yet";
  if (kind === "read_only") return "Read-only — everything you made is still here";
  if (kind === "trial" && e.ends_at) return `Free trial — ends ${longDate(e.ends_at)}`;
  return planLabel(e);
}

const n = (x: number) => x.toLocaleString("en-GB");

export function usageMeters(e: Entitlement) {
  const pages = e.meters.find((m) => m.name === "pages_ingested");
  const papers = e.meters.find((m) => m.name === "papers_generated");
  return { pages, papers };
}

export function PlanBadge({
  entitlement,
  onOpen,
}: {
  entitlement: Entitlement;
  onOpen: () => void;
}) {
  // Never had a plan: nothing to report and nothing to read. The chooser under the header
  // already says what to do; "Read-only" here told a first-time stranger their account was
  // restricted (seen on the account, 2 (c) Task 13). Lapsed and expired keep "Read-only".
  if (kindOf(entitlement) === "none") return null;
  if (kindOf(entitlement) === "read_only")
    return (
      <span className="bar__plan">
        <button
          type="button"
          className="pill pill--plain"
          onClick={onOpen}
          title="Your plan and allowances"
        >
          Read-only
        </button>
      </span>
    );
  const { pages, papers } = usageMeters(entitlement);
  const shown = [pages, papers].filter((m) => m !== undefined);
  const usage = [
    pages && `${n(pages.used)} of ${n(pages.limit)} pages`,
    papers && `${n(papers.used)} of ${n(papers.limit)} papers`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span className="bar__plan">
      <button type="button" className="quota" onClick={onOpen} title="Your plan and allowances">
        {planLabel(entitlement)}
      </button>
      {shown.length > 0 && <MiniMeters meters={shown} />}
      {usage && <span className="quota__usage">{usage}</span>}
    </span>
  );
}
